import React, { useEffect, useState } from 'react';
import {  getInitialLang } from '../Common';
import { checkIfHasInvestorAccess } from './support/checkIfHasInvestorAccess'
import { $MAX_WIDTH } from '../Consts';
import { createToken } from './support/createToken';

const NAV_HEIGHT = 56;

const IconClose = ({ color = '#fff', size = 26 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconDownload = ({ color = '#0061FF', size = 22 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
  </svg>
);

const IconBriefcase = ({ color = 'rgb(50, 153, 255)', size = 36 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 3H8a2 2 0 00-2 2v2h12V5a2 2 0 00-2-2z" />
    <line x1="2" y1="13" x2="22" y2="13" />
  </svg>
);

const s: Record<string, React.CSSProperties> = {
  page: {
    width: '100%',
    minHeight: '100vh',
    boxSizing: 'border-box',
    padding: `calc(${NAV_HEIGHT+10}px + 3vh) 1rem 3vh`,
    color: '#09213D',
    fontFamily: 'Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: '100%',
    maxWidth: $MAX_WIDTH,
    background: '#fff',
    borderRadius: 18,
    padding: '2.5rem 4rem 3rem',
    boxShadow: '0 12px 32px rgba(0,0,0,.14),0 24px 64px rgba(0,0,0,.12)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2.5rem',
    flex: 1,
    maxHeight: '100%',
    overflow: 'auto',
    boxSizing: 'border-box',
  },
  h1: {
    fontSize: '2.5rem',
    marginBottom: 0,
    fontWeight: 800,
    background: 'linear-gradient(90deg, #45A6FF 0%, #1F8BFF 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.6rem',
  },
  p: {
    fontSize: '1.125rem',
    lineHeight: 1.6,
    textAlign: 'center',
    maxWidth: 720,
    margin: '0 auto',
    color: '#1B2C48',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '0.5rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
    gap: '1.75rem',
    marginTop: '.5rem',
  },
  feat: {
    display: 'flex',
    alignItems: 'center',  
    gap: '.8rem',
    fontSize: '1rem',
    lineHeight: 1.5,
    background: '#FFFFFF',
    padding: '1.3rem 1.2rem',
    borderRadius: 14,
    boxShadow: '0 6px 20px rgba(0,0,0,.06)',
    color: '#1B2C48',
  },
  btnRow: { display: 'flex', flexWrap: 'wrap', gap: '1.25rem', justifyContent: 'center', marginTop: '1rem' },
  btn: {
    padding: '1rem 3rem',
    fontSize: '1.075rem',
    fontWeight: 700,
    borderRadius: 10,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    border: 0,
    transition: 'transform .15s ease,box-shadow .15s ease',
    gap: '.55rem',
  },
  primary: {
    background: 'linear-gradient(135deg,#0A7CFF 0%,#0061FF 100%)',
    color: '#fff',
    boxShadow: '0 4px 16px rgba(10,124,255,.45)',
  },
  outline: {
    background: 'transparent',
    color: '#0061FF',
    border: '2px solid #0061FF',
  },
  link: {
    textDecoration: 'none',
    padding: 0,
    fontSize: '1.05rem',
    fontWeight: 700,
    borderRadius: 10,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '.55rem',
    transition: 'transform .15s ease',
    color: '#0061FF',
    background: 'transparent',
  },
  appendixRow: {
    marginTop: 'auto',
    display: 'flex',
    gap: '1rem',
    alignSelf: 'flex-end',
    width: '100%'
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,.65)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3vh 3vw',
    zIndex: 10000,
  },
  modal: {
    position: 'relative',
    width: 'min(94vw,1200px)',
    height: 'min(88vh,820px)',
    borderRadius: 14,
    overflow: 'hidden',
    background: '#000',
    boxShadow: '0 12px 48px rgba(0,0,0,.35)',
  },
  iframe: { width: '100%', height: '100%', border: 'none' },
  close: {
    position: 'absolute',
    top: 14,
    right: 14,
    background: 'rgba(0,0,0,.45)',
    border: 'none',
    borderRadius: '50%',
    padding: 6,
    cursor: 'pointer',
    lineHeight: 0,
  },
};

const mobileCSS = `
@media (max-width: 640px) {
  .bp-page {
    display: block !important;
    height: auto !important;
    min-height: auto !important;
    padding: ${NAV_HEIGHT + 16}px 1rem 2rem !important;
  }
  .bp-card {
    width: 100% !important;
    max-height: none !important;
    padding: 3rem 1.5rem !important;
    box-sizing: border-box;
  }
  .bp-grid {
    grid-template-columns: 1fr !important;
  }
  .bp-appendix {
    align-self: stretch !important;
    justify-content: center !important;
    margin-top: 2rem !important;
  }
}
`;

const EN = {
  title: 'BusinessA Business Plan',
  desc: 'Expands on the pitch deck\'s highlights with strategic rationale, finer solution details, a deeper dive into forecasts and economics and well-sourced data for key assumptions',
  features: [
    'In-depth value prop, market opportunity & competition analysis',
    'Business model, unit economics & financial projections',
    'Clear ICP, Beachhead Segment and Go-to-market strategy',

    'In-depth Risks & Mitigations Register',
    'Detailed team structure & five year roadmap',
    'Detailed funding ask & usage breakdown',
  ],
  view: 'View',
  download: 'Download',
  fileName: 'ba_business_plan.pdf',
  viewAppendix: 'View Appendix',
  downloadAppendix: 'Appendix',
  appendixFileName: 'ba_business_plan_appendix.pdf',
};

const ZH = {
  title: 'BusinessA 商业计划书',
  desc: '探索我们的全面路线图，涵盖市场分析、上市策略以及详细的财务预测。',
  features: ['深入的市场洞察与竞争定位', '清晰的上市策略与里程碑追踪', '五年财务模型和收入预测'],
  view: '查看',
  download: '下载',
  fileName: 'ba_business_plan_cn.pdf',
  viewAppendix: '查看附录',
  downloadAppendix: '下载附录',
  appendixFileName: 'ba_business_plan_appendix_cn.pdf',
};

const PDFS: Record<'en' | 'zh', string> = {
  en: 'https://serve.businessa.com/ba_business_plan.pdf',
  zh: 'https://serve.businessa.com/ba_business_plan_cn.pdf',
};

const APPENDIX_PDFS: Record<'en' | 'zh', string> = {
  en: 'https://serve.businessa.com/ba_business_plan_appendix.pdf',
  zh: 'https://serve.businessa.com/ba_business_plan_appendix_cn.pdf',
};

const LANGS = { en: EN, zh: ZH };

const BusinessPlanPage: React.FC = () => {
  const [lang] = useState<'en' | 'zh'>(getInitialLang);
  const [hasInvestorAccess] = useState<boolean>(() => checkIfHasInvestorAccess());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (hasInvestorAccess) return;

    const authUrl = new URL('/auth.html', window.location.origin);
    authUrl.searchParams.set('url', window.location.href);

    window.location.replace(authUrl.href);
  }, [hasInvestorAccess]);

  if (!hasInvestorAccess) return null;

  const t = LANGS[lang];
  const pdfURL = PDFS[lang];
  const appendixURL = APPENDIX_PDFS[lang] + '?token=' + createToken() + '&download=1';
  
  const download = () => {
    const a = document.createElement('a');
    a.href = pdfURL;
    a.download = t.fileName + '?token=' + createToken()+ '&download=1';
    a.target = '_blank';
    a.click();
  };

  const downloadAppendix = () => {
    const a = document.createElement('a');
    a.href = appendixURL;
    a.download = t.appendixFileName + '?token=' + createToken()+ '&download=1';
    a.target = '_blank';
    a.click();
  };

  /* ─────────────────────────────  NEW FEATURE ICONS  ─────────────────────────── */

const IconAnalysis = ({ color = '#0061FF', size = 28 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }}  
       >
    {/* Magnifying glass */}
    <circle cx="11" cy="11" r="7" />
    <line x1="16.65" y1="16.65" x2="21" y2="21" />
    {/* Bars = market / comp analysis */}
    <line x1="8"  y1="13" x2="8"  y2="11" />
    <line x1="11" y1="13" x2="11" y2="9"  />
    <line x1="14" y1="13" x2="14" y2="10" />
  </svg>
);

const IconEconomics = ({ color = '#0061FF', size = 28 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }}  
       >
    {/* Axis */}
    <line x1="3"  y1="21" x2="21" y2="21" />
    {/* Unit-economics / projections bars */}
    <line x1="7"  y1="21" x2="7"  y2="9"  />
    <line x1="12" y1="21" x2="12" y2="5"  />
    <line x1="17" y1="21" x2="17" y2="12" />
  </svg>
);

const IconTarget = ({ color = '#0061FF', size = 28 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }}  
       >
    {/* Bullseye */}
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="3" />
    {/* Arrow hitting ICP / beachhead */}
    <line     x1="21" y1="3"  x2="13.5" y2="10.5" />
    <polyline points="19 3 21 3 21 5" />
  </svg>
);

const IconShield = ({ color = '#0061FF', size = 28 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }}  
       >
    {/* Shield outline */}
    <path d="M12 2 L20 6 V12 C20 17 16 21 12 22 C8 21 4 17 4 12 V6 L12 2 Z" />
    {/* Check  = mitigated risk */}
    <polyline points="9 12 11.5 14.5 15 11" />
  </svg>
);

const IconTeam = ({ color = '#0061FF', size = 28 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }}  
       >
    {/* People */}
    <circle cx="12" cy="7"  r="3" />
    <circle cx="6"  cy="10" r="2" />
    <circle cx="18" cy="10" r="2" />
    {/* Bodies */}
    <path d="M12 10c-4 0-6 2-6 4v2h12v-2c0-2-2-4-6-4z" />
    <path d="M6 12c-2 0-3 1-3 2v2" />
    <path d="M18 12c 2 0 3 1 3 2v2" />
  </svg>
);

// 💰 Funding / $ Ask
const IconFunding = (
  { color = '#0061FF', size = 28 }: { color?: string; size?: number }
) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    {/* dollar icon (filled glyph) */}
    <path
      d="M7,15H9C9,16.08 10.37,17 12,17C13.63,17 15,16.08 15,15C15,13.9 13.96,13.5 11.76,12.97C9.64,12.44 7,11.78 7,9C7,7.21 8.47,5.69 10.5,5.18V3H13.5V5.18C15.53,5.69 17,7.21 17,9H15C15,7.92 13.63,7 12,7C10.37,7 9,7.92 9,9C9,10.1 10.04,10.5 12.24,11.03C14.36,11.56 17,12.22 17,15C17,16.79 15.53,18.31 13.5,18.82V21H10.5V18.82C8.47,18.31 7,16.79 7,15Z"
      fill={color}
      stroke="none"
    />
  </svg>
);


/* ───────────────────────────  UPDATE INSIDE COMPONENT  ────────────────────── */

// …inside BusinessPlanPage (replace the existing `icons` array)
const icons = [
  <IconAnalysis  />, // In-depth value prop, market opportunity & competition analysis
  <IconEconomics />, // In-depth business model, unit economics & financial projections
  <IconTarget    />, // Clear ICP, Beachhead Segment and Go-to-market strategy
  <IconShield    />, // Detailed Risks & Mitigations Register
  <IconTeam      />, // Detailed team structure & five year roadmap
  <IconFunding   />, // Detailed funding ask & usage breakdown
];
  const features = t.features.map((text, i) => ({ icon: icons[i], text }));

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: mobileCSS }} />
      <div style={s.page} className="bp-page">
        <div style={s.card} className="bp-card">
          <h1 style={s.h1}>
            <IconBriefcase />
            {t.title}
          </h1>
          <p style={s.p}>{t.desc}</p>

          <div style={s.grid} className="bp-grid">
            {features.map((f, i) => (
              <div key={i} style={s.feat}>
                {f.icon}
                {f.text}
              </div>
            ))}
          </div>

          <div style={s.btnRow}>
            <button
              style={{ ...s.btn, ...s.primary }}
              onClick={() => setOpen(true)}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              {t.view}
            </button>
            <button
              style={{ ...s.btn, ...s.outline }}
              onClick={download}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <IconDownload color="#0061FF" />
              {t.download}
            </button>
          </div>

          <div style={s.appendixRow} className="bp-appendix">
            <div style={{ flex: 1 }} />
            <a
              href={appendixURL}
              download={t.appendixFileName + '?token=' + createToken()+ '&download=1'}
              style={{ ...s.link, fontSize: 15 }}
              onClick={(e) => {
                e.preventDefault();
                downloadAppendix();
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <IconDownload color="#0061FF" size={18} />
              {t.downloadAppendix}
            </a>
            <div style={{ flex: 1 }} />

          </div>
        </div>

        {open && (
          <div style={s.overlay} onClick={() => setOpen(false)}>
            <div style={s.modal} onClick={(e) => e.stopPropagation()}>
              <button style={s.close} onClick={() => setOpen(false)}>
                <IconClose />
              </button>
              <iframe title="BusinessA Business Plan" src={pdfURL} style={s.iframe} loading="lazy" />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BusinessPlanPage;
